import { useState, useEffect, useCallback } from 'react';
import { LifeBuoy, MessageCircle, X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Résultat d'un mot de passe temporaire : à copier / envoyer par WhatsApp.
export const TempPasswordResult = ({ result, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="temp-password-modal">
    <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.40)] p-6 w-full max-w-md">
      <h3 className="text-white text-lg mb-1">Mot de passe temporaire généré</h3>
      <p className="text-zinc-400 text-sm mb-4">{result.customer.name} — {result.customer.phone}</p>
      <div className="flex items-center justify-between bg-black border border-white/10 px-4 py-3 mb-4">
        <span className="text-[color:var(--color-primary)] text-2xl tracking-[0.3em] font-mono" data-testid="temp-password-value">{result.temp_password}</span>
        <button type="button" onClick={() => { navigator.clipboard?.writeText(result.temp_password); toast.success('Copié'); }}
          className="text-zinc-400 hover:text-white" title="Copier"><Copy className="w-4 h-4" /></button>
      </div>
      <p className="text-zinc-500 text-xs mb-4">Le client devra changer ce mot de passe dans « Mon compte ». Il n'est affiché qu'une seule fois.</p>
      <div className="flex gap-3 justify-end">
        <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 text-zinc-400 hover:text-white text-sm">Fermer</button>
        <a href={result.whatsapp_url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 text-sm hover:bg-[#20BD5A]" data-testid="temp-password-whatsapp">
          <MessageCircle className="w-4 h-4" /> Envoyer par WhatsApp
        </a>
      </div>
    </div>
  </div>
);

export const useTempPassword = () => {
  const [result, setResult] = useState(null);
  const generate = useCallback(async (url) => {
    try {
      const res = await axios.post(url);
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la génération');
    }
  }, []);
  return { result, generate, clear: () => setResult(null) };
};

// Demandes « mot de passe oublié » des clients sans e-mail.
export const RecoveryRequestsPanel = ({ onResolved }) => {
  const [requests, setRequests] = useState([]);
  const { result, generate, clear } = useTempPassword();

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/admin/recovery-requests`).then(r => setRequests(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const dismiss = async (id) => {
    await axios.delete(`${API_URL}/api/admin/recovery-requests/${id}`).catch(() => {});
    load();
  };

  if (requests.length === 0) return null;

  return (
    <section className="mb-6 border border-amber-500/40 bg-amber-500/5 p-4" data-testid="recovery-requests-panel">
      <h2 className="text-amber-200 text-sm uppercase tracking-widest flex items-center gap-2 mb-3">
        <LifeBuoy className="w-4 h-4" /> Demandes de récupération de compte ({requests.length})
      </h2>
      <ul className="space-y-2">
        {requests.map(r => (
          <li key={r.id} className="flex flex-wrap items-center gap-3 bg-black/40 px-3 py-2" data-testid={`recovery-request-${r.id}`}>
            <div className="flex-1 min-w-[12rem] basis-full sm:basis-auto">
              <p className="text-white text-sm">{r.name} <span className="text-zinc-500">— {r.phone}</span></p>
              <p className="text-zinc-500 text-xs">{new Date(r.created_at).toLocaleString('fr-FR')}{r.email ? ' · e-mail en échec' : ' · pas d\'e-mail'}</p>
            </div>
            <button type="button" onClick={() => generate(`${API_URL}/api/admin/recovery-requests/${r.id}/temp-password`)}
              className="inline-flex items-center gap-2 bg-[color:var(--color-primary)] text-black text-xs px-3 py-1.5 hover:bg-[color:var(--color-primary-hover)]"
              data-testid={`recovery-resolve-${r.id}`}>
              <MessageCircle className="w-3.5 h-3.5" /> MDP temporaire + WhatsApp
            </button>
            <button type="button" onClick={() => dismiss(r.id)} className="text-zinc-500 hover:text-white" title="Ignorer"
              data-testid={`recovery-dismiss-${r.id}`}><X className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
      {result && <TempPasswordResult result={result} onClose={() => { clear(); load(); onResolved?.(); }} />}
    </section>
  );
};
